import React, { useContext, useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { addComment, updateComment } from "../api/comments";
import { UserContext } from '../functions/Auth';
import { fnSetIsEditorEmpty, extractTextFromJson } from '../utils';

// component
import ConfirmDialog from '../components/ConfirmDialog';
import FullScreenModal from './FullScreenModal';
import Lexical from './Lexical';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

type propsType = {
    fnGetTopicComments: (topicId: string) => void,
    isEdit: boolean,
    isOpen: boolean,
    isReply: boolean,
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setIsReply: React.Dispatch<React.SetStateAction<boolean>>,
    targetEditComment: number,
    targetEditCommentEditorState: string | undefined,
    targetReplyCommentNodeAsJSON: string,
    targetReplyUser: {userId: number, userName: string},
};

type CommentType = {
    body: string,
    userId: number,
}

export default function CommentModal(props: propsType) {
    const {
        fnGetTopicComments,
        isEdit,
        isOpen,
        isReply,
        setIsEdit,
        setIsOpen,
        setIsReply,
        targetEditComment,
        targetEditCommentEditorState,
        targetReplyCommentNodeAsJSON,
        targetReplyUser,
    } = props;
    const params = useParams();
    const topicId = params.topicId as string;
    const userInfo = useContext(UserContext);
    const userId = userInfo?.userId as number;

    // エディター
    const [currentContentAsJSON, setCurrentContentAsJSON] = useState<string>('');
    const [isEditorEmpty, setIsEditorEmpty] = useState<boolean>(false);

    // エディターが空かどうかの値をセット
    useEffect(() => {
        if (currentContentAsJSON) {
            fnSetIsEditorEmpty(currentContentAsJSON, setIsEditorEmpty);
        }
    }, [currentContentAsJSON]);

    // 投稿、更新時のリクエストボディ生成
    const fnCreateCommentContent = (currentContentAsJSON: string) => {
        return {
            body: currentContentAsJSON,
            userId: userId,
        }
    }

    // バリデーションメッセージ
    const [validationMsg, setValidationMsg] = useState <string>("");

    // 返信
    const [replyContent, setReplyContent] = useState<{ message: string, userName: string, userId: number } | undefined>(undefined);

    useEffect(() => {
        if (isReply) {
            const replyMessage = extractTextFromJson(targetReplyCommentNodeAsJSON, 64);
            if (replyMessage) {
                setReplyContent({
                    message: replyMessage,
                    userName: targetReplyUser.userName,
                    userId: targetReplyUser.userId,
                });
            }
        } else {
            setReplyContent(undefined);
        }
    }, [isOpen, isReply, targetReplyCommentNodeAsJSON, targetReplyUser])

    // コメントの追加
    const fnAddComment = async (commentContent: CommentType) => {
        const result = await addComment(topicId, commentContent);
        setValidationMsg("");
        if (!result) { return false }
        // 検証エラー
        if ("error" in result) {
            if (result.error.validationErrors) {
                setValidationMsg(result.error.validationErrors[0].message);
            }
        }
        // success
        if (result.status === 200) {
            fnGetTopicComments(topicId);
            setIsReply(false);
            setIsOpen(false);
        }
    }

    // コメントの編集
    const fnEditComment = async (commentId: string, topicId: string, content: CommentType) => {
        const result = await updateComment(commentId, topicId, content);
        setValidationMsg("");
        if (!result) { return false }
        // 検証エラー
        if ("error" in result) {
            if (result.error.validationErrors) {
                setValidationMsg(result.error.validationErrors[0].message);
            }
        }
        // success
        if (result.status === 200) {
            fnGetTopicComments(topicId);
            setIsEdit(false);
            setIsOpen(false);
        }
    };

    /**
     * 確認ダイアログ
     */
    const [agreeButtonDisplay, setAgreeButtonDisplay] = useState("");
    const [confirmDialogBody, setConfirmDialogBody] = useState<string | JSX.Element>("");
    const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
    const [handleConfirmDialogAgree, setHandleConfirmDialogAgree] = useState<() => void>(() => { });
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const handleAddCommentAgree = () => {
        if (isEditorEmpty) {
            setValidationMsg("本文を入力してください。");
            return false;
        }
        const commentContent = fnCreateCommentContent(currentContentAsJSON);
        fnAddComment(commentContent);
    }
    const handleEditCommentAgree = () => {
        if (isEditorEmpty) {
            setValidationMsg("本文を入力してください。");
            return false;
        }
        const commentContent = fnCreateCommentContent(currentContentAsJSON);
        fnEditComment(targetEditComment.toString(), topicId, commentContent);
    }
    const handleCancelCommentAgree = () => {
        setIsOpen(false);
        setIsEdit(false);
        setIsReply(false);
        setValidationMsg("");
    }
    const handleConfirmDialogDisagree = () => {
        setAgreeButtonDisplay("");
        setIsOpenConfirmDialog(false);
        setConfirmDialogBody("");
        setConfirmDialogTitle("");
        setHandleConfirmDialogAgree(() => { });
    }
    const handleCloseModal = (isEdit: boolean) => {
        if (isEdit) {
            setAgreeButtonDisplay("OK");
            setConfirmDialogBody("キャンセルすると編集途中の内容は反映されません。");
            setConfirmDialogTitle("編集をキャンセルしますか？");
        }
        else {
            setAgreeButtonDisplay("OK");
            setConfirmDialogBody("キャンセルすると作成途中の内容は反映されません。");
            setConfirmDialogTitle("投稿をキャンセルしますか？");
        }
        setHandleConfirmDialogAgree(() => handleCancelCommentAgree);
        setIsOpenConfirmDialog(true);
    }

    return (
        <FullScreenModal
            fnOnClose={() => handleCloseModal(isEdit)}
            isOpen={isOpen}
        >
            <Box
                sx={{
                    textAlign: "center",
                }}
            >
                <Typography
                    id="modal-modal-title"
                    variant="h1"
                    component="h1"
                    fontWeight="600"
                    sx={{ fontSize: { mb: 20, tb: 24, pc: 32 } }}
                >
                    コメントを{isEdit ? "編集" : "投稿"}
                </Typography>
            </Box>
            <Box>
                {/*本文*/}
                <Lexical
                    error={!!validationMsg}
                    helperText={validationMsg}
                    defaultContentAsJSON={targetEditCommentEditorState}
                    isEditable={true}
                    replyContent={replyContent}
                    setCurrentContentAsJSON={setCurrentContentAsJSON}
                />
                {/*ボタン*/}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: { mb: "wrap", tb: "nowrap" },
                        mt: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => handleCloseModal(isEdit)}
                        size="small"
                        sx={{
                            width: { mb: "calc(50% - 8px)", tb: "unset" },
                            ml: 2,
                        }}
                    >
                        キャンセル
                    </Button>
                    <Button
                        onClick={() => {
                            if (isEdit) {
                                setHandleConfirmDialogAgree(() => handleEditCommentAgree);
                            }
                            else {
                                setHandleConfirmDialogAgree(() => handleAddCommentAgree);
                            }
                            setAgreeButtonDisplay("投稿する");
                            setConfirmDialogBody("コメント投稿後も編集することができます。");
                            setConfirmDialogTitle("内容を投稿してよろしいですか？");
                            setIsOpenConfirmDialog(true);
                        }}
                        size="small"
                        sx={{
                            width: { mb: "100%", tb: "unset" },
                            ml: { mb: 0, tb: 2 },
                            mt: { mb: 1.5, tb: 0 },
                        }}
                        variant="contained"
                    >
                        投稿する 
                    </Button>
                </Box>
            </Box>

            {/*各確認ダイアログ*/}
            <ConfirmDialog
                agreeButtonDisplay={agreeButtonDisplay}
                body={confirmDialogBody}
                handleClickAgree={handleConfirmDialogAgree}
                handleClickDisagree={handleConfirmDialogDisagree}
                isOpen={isOpenConfirmDialog}
                setIsOpen={setIsOpenConfirmDialog}
                title={confirmDialogTitle}
            />
        </FullScreenModal>
    );
}